import { getValidatedResumePathForApplication } from "../../util/authResume";
import { doGET } from "../../util/HttpUtil";
import { FORM_TYPE } from "../../util/applicationLabels";
import {
  AUTH_V2_FORM_TYPES,
  LOGIN_V2_DECISION,
  resolveRouteInfoForApplication,
} from "./loginV2Decision";

const getCurrentHref = () => (typeof window === "undefined" ? "" : window.location.href);

export const buildQueryString = (params = {}) => (
  Object.entries(params)
    .filter(([, value]) => value !== "")
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&")
);

export const withNoCacheParam = (url, timestamp = Date.now()) => {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}_ts=${timestamp}`;
};

export const getLoginApplicationId = (response) =>
  response?.data?.userContent?.application_id || response?.data?.userContent?._id || "";

export const resolveUnifiedBusinessType = ({ form = {}, currentUrl = getCurrentHref() } = {}) => {
  const parsedUrl = currentUrl ? new URL(currentUrl, "http://localhost") : null;
  const typeFromQuery = Number(parsedUrl?.searchParams?.get("type"));
  if (!Number.isNaN(typeFromQuery) && [1, 2, 3].includes(typeFromQuery)) {
    return typeFromQuery;
  }
  if ([1, 2, 3].includes(form?.type)) {
    return form.type;
  }
  if ([1, 2, 3].includes(form?.defaultType)) {
    return form.defaultType;
  }
  return 1;
};

export const isUnifiedBusinessLoginForm = (form = {}) => form?.apiPath === "business";

export const shouldUseV2LoginForForm = (form = {}) =>
  Boolean(form?.formType && form?.formType !== "EDD" && form?.type !== "kyc");

export const resolveUnifiedBusinessFormType = ({
  form = {},
  unifiedBusinessType,
  isUnifiedBusinessLogin = isUnifiedBusinessLoginForm(form),
} = {}) => (
  isUnifiedBusinessLogin && Number(unifiedBusinessType) !== 3
    ? AUTH_V2_FORM_TYPES.BUSINESS_PLUS
    : form?.formType
);

export const buildApplicationLoginData = ({ data = {}, form = {}, otpId, currentUrl } = {}) => {
  const unifiedBusinessType = resolveUnifiedBusinessType({ form, currentUrl });
  const isUnifiedBusinessLogin = isUnifiedBusinessLoginForm(form);
  const unifiedBusinessFormType = resolveUnifiedBusinessFormType({
    form,
    unifiedBusinessType,
    isUnifiedBusinessLogin,
  });
  let loginData = {
    otpId,
    userType: "customer",
    otpValue: data?.otpValue,
  };

  if (data?.email) {
    loginData.email = data.email;
  } else {
    throw new Error("Email is required for login.");
  }

  if (form?.formType) {
    loginData.formType = unifiedBusinessFormType;
    loginData.loginType = "APPLICATION_LOGIN";
  }
  if (isUnifiedBusinessLogin) {
    loginData.type = unifiedBusinessType;
  }

  if (data?.gTagUtmData) {
    loginData = { ...(loginData ?? {}), ...(data?.gTagUtmData ?? {}) };
  }

  if (data?.utm_referrer) {
    loginData = { ...(loginData ?? {}), utm_referrer: data?.utm_referrer };
  }

  return {
    loginData,
    unifiedBusinessType,
    unifiedBusinessFormType,
    isUnifiedBusinessLogin,
  };
};

export const buildCreateQueryString = (data = {}) => {
  const queryString = data?.gTagUtmData ? buildQueryString(data.gTagUtmData) : "";
  return `${data?.utm_referrer ? `utm_referrer=${data?.utm_referrer}&` : ""}${queryString ? `${queryString}` : ""}`;
};

export const buildV2BlockedMessage = (decision = {}) => {
  const blockedApplicationReason = decision?.blockedApplications?.find((item) => item?.blockedReason)?.blockedReason;
  const blockedCreateReason = decision?.blockedCreateOptions?.find((item) => item?.blockedReason)?.blockedReason;

  if (decision?.routeMismatchedAllowedCreateOptions?.length) {
    return "This email is linked to a different application type. Please use the matching login page.";
  }

  return blockedApplicationReason || blockedCreateReason || "No applications are available for this login.";
};

export const isBlockedLoginV2Decision = (decision = {}) => decision?.type === LOGIN_V2_DECISION.BLOCKED;

export const resolveBusinessRouteInfo = async ({
  applicationId,
  fallbackType = null,
  form = {},
  fetchApplicationForm = doGET,
} = {}) => {
  if (!applicationId) {
    return null;
  }

  try {
    const applicationResponse = await fetchApplicationForm(
      withNoCacheParam(`/api/application-form/detail?id=${applicationId}&withExtension=true`)
    );
    return resolveRouteInfoForApplication({
      ...(applicationResponse?.data || {}),
      application_id: applicationId,
      type: applicationResponse?.data?.type ?? fallbackType,
    }, form);
  } catch (error) {
    return resolveRouteInfoForApplication({
      application_id: applicationId,
      formType: Number(fallbackType) === 3 ? FORM_TYPE.BusinessPrime : FORM_TYPE.Business,
      applicationFormType: FORM_TYPE.Business,
      type: fallbackType,
    }, form);
  }
};

export const buildFinalAuthNavigationTarget = ({
  response,
  applicationOption = {},
  fallbackFormType,
  fallbackType,
  form = {},
} = {}) => {
  const applicationId = getLoginApplicationId(response) || applicationOption?.applicationId || applicationOption?.application_id;
  if (!applicationId) {
    return null;
  }

  const routeInfo = resolveRouteInfoForApplication({
    ...applicationOption,
    application_id: applicationId,
    formType: applicationOption?.formType || response?.data?.userContent?.formType || fallbackFormType,
    applicationFormType: applicationOption?.applicationFormType || response?.data?.userContent?.formType || fallbackFormType,
    type: applicationOption?.type ?? response?.data?.userContent?.type ?? fallbackType,
  }, form);
  const resumePath = routeInfo?.url
    ? ""
    : getValidatedResumePathForApplication({
      form,
      applicationId,
    });

  return {
    applicationId,
    url: routeInfo?.url || resumePath || `/${form?.path}/${applicationId}`,
    config: { replace: true, ...(routeInfo?.state && { state: routeInfo.state }) },
    formType: routeInfo?.formType || fallbackFormType || form?.formType,
  };
};

export const buildLegacyCreateRequest = ({
  data = {},
  form = {},
  selectedCreateOption = {},
  unifiedBusinessType,
} = {}) => {
  const selectedType = selectedCreateOption?.type ?? unifiedBusinessType;
  const isUnifiedBusinessLogin = isUnifiedBusinessLoginForm(form);
  return {
    endpoint: `/api/${form?.apiPath}/create?${buildCreateQueryString(data)}`,
    payload: isUnifiedBusinessLogin
      ? { email: data.email, type: selectedType }
      : { email: data.email, ...(form?.isPrime && { interestedInPrime: true }) },
    selectedType,
  };
};
